import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import { datadogRum } from '@datadog/browser-rum';
import { UserContext } from '~/dashboard/context';
import { usePolicyContext } from './usePolicyContext';
import { AIAnalysisContainer, StyledIframe, StyledLoadingText } from './styles';
import type { Dimension } from '~/application-review/redux/types';
import { useAppSelector } from '~/store/reduxHooks';
import { selectApplicationPage } from '~/application-review/redux/selectors';
import type { State } from '~/store';

const IFRAME_DEFAULT_HEIGHT = 58;

const DefaultWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => children;

interface AiSummaryCardProps {
  token: string;
  type?: string;
  style?: React.CSSProperties;
  /** Entity token for fetching rule explainability context */
  entityToken?: string;
  /** Workflow token for fetching workflow data */
  workflowToken?: string;
  /** Workflow version number */
  workflowVersion?: number;
  /** Whether this is a portfolio evaluation */
  isPortfolioEvaluation?: boolean;
  /** Dimensions for portfolio evaluations */
  dimensions?: Dimension[];
  Wrapper?: React.FC<{ children: React.ReactNode }>;
}

export const AiSummaryCard = ({
  token,
  type,
  style = {},
  entityToken,
  workflowToken,
  workflowVersion,
  isPortfolioEvaluation = false,
  dimensions = [],
  Wrapper = DefaultWrapper,
}: AiSummaryCardProps) => {
  const {
    agent: { isEmployee },
    featureFlags: {
      llmAiAnalysisAdminOverride,
      llmEvaluationSummaryEmbedded,
      llmLinkedEntitiesAnalysis,
      llmFraudDiligenceAnalysis,
      llmSummariesJa,
      llmSummariesInvestigation,
    },
  } = useContext(UserContext);
  const { application } = useAppSelector(selectApplicationPage) ?? {};
  const { investigationType } = useAppSelector((state: State) => (
    state?.investigations?.investigation
  )) ?? {};

  const isEvaluation = /^[LS]-/.test(token);
  const isJourneyApplication = /^JA-/.test(token);
  const isInvestigation = /^INV-/.test(token);

  const canViewEvaluation = isEvaluation && llmEvaluationSummaryEmbedded;
  const isNestedEvaluation = canViewEvaluation && (
    (Boolean(application) && llmSummariesJa) || Boolean(investigationType)
  );

  const canViewJourneyApplication = isJourneyApplication && llmSummariesJa;
  const canViewInvestigation = isInvestigation && llmSummariesInvestigation;

  const canView = canViewEvaluation || canViewJourneyApplication || canViewInvestigation;

  const isAdminOverride = (
    llmAiAnalysisAdminOverride
    && (isEvaluation || isJourneyApplication || isInvestigation)
    && !canView
    && isEmployee
  );

  const isInline = isNestedEvaluation;

  // Determine if we should fetch and send policy context
  const shouldUsePolicyContext = Boolean(
    entityToken && workflowToken && workflowVersion,
  );

  const queryParams = new URLSearchParams();

  if (type) {
    queryParams.set('type', type);
  }

  // Add policyContext param to signal embedded dashboard to wait for postMessage
  if (shouldUsePolicyContext) {
    queryParams.set('policyContext', 'true');
  }

  if (isInline) {
    queryParams.set('inline', 'true');
  }

  queryParams.set(
    'includeLinkedEntities',
    String(isEvaluation && llmLinkedEntitiesAnalysis),
  );

  queryParams.set(
    'includeFraudDiligence',
    String(isEvaluation && llmFraudDiligenceAnalysis),
  );

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(IFRAME_DEFAULT_HEIGHT);
  const [iframeReady, setIframeReady] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Use policy context hook to generate and send rule document
  const {
    ruleDocument,
    isLoading: policyContextLoading,
    sendToIframe,
  } = usePolicyContext({
    entityToken: entityToken || '',
    evaluationToken: token,
    workflowToken: workflowToken || null,
    workflowVersion: workflowVersion ?? null,
    isPortfolioEvaluation,
    dimensions,
    enabled: shouldUsePolicyContext,
  });

  const onMessage = useCallback(({ data: message, origin, source }) => {
    if (source !== iframeRef.current?.contentWindow) {
      return;
    }

    if (origin !== window.location.origin) {
      return;
    }

    if (message?.source !== 'embedded-dashboard') {
      return;
    }

    switch (message.type) {
      case 'ready':
        setIsLoading(false);
        break;
      case 'resize':
        setIframeHeight(message.data.height);
        break;
      case 'readyForPolicyContext':
        // Iframe is ready to receive policy context
        setIframeReady(true);
        break;
      case 'analytics':
        // Forward embedded-dashboard interaction events into this host's RUM, which
        // already identifies the user, so they can be attributed per user.
        datadogRum.addAction(message.name);
        break;
      default:
        break;
    }
  }, [setIframeHeight]);

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  // Send policy context to iframe when ready
  useEffect(() => {
    if (
      shouldUsePolicyContext
      && iframeReady
      && ruleDocument
      && !policyContextLoading
    ) {
      sendToIframe(iframeRef);
    }
  }, [shouldUsePolicyContext, iframeReady, ruleDocument, policyContextLoading, sendToIframe]);

  if (!canView && !isAdminOverride) {
    return null;
  }

  return (
    <Wrapper>
      <AIAnalysisContainer className="ai-analysis" style={style} direction="column" gap={2} $isAdmin={isAdminOverride}>
        {isAdminOverride && (
          <Box justifyContent="flex-start">
            <Icon icon={IconName.Eye} size="small" color="var(--cl-text-secondary)" />
            <Text size={1} style={{ color: 'var(--cl-text-secondary)' }}>
              AI Analysis is disabled for this customer. Only visible to Alloy Admins.
            </Text>
          </Box>
        )}
        <StyledIframe
          ref={iframeRef}
          width="100%"
          height={iframeHeight}
          src={`${window.origin}/embedded/summary/${token}?${queryParams.toString()}`}
          $isLoading={isLoading}
          $isInline={isInline}
        />
        {isLoading && (
          <StyledLoadingText>Loading</StyledLoadingText>
        )}
      </AIAnalysisContainer>
    </Wrapper>
  );
};
